import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { customThemeWithHeaders } from "@/pages/adjustments/manager/components/table/AdjustmentsManagerTable";
import { dxtToLocalServerTime, useContextMenuMESF } from "@dexteel/mesf-core";
import { Box, Popover, Typography } from "@mui/material";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
	RowClassParams,
	RowClickedEvent,
	RowDoubleClickedEvent,
	RowStyle,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Cobble } from "../models/Cobble";
import { ConfirmActionModal } from "./ConfirmActionModal";
import { useCobbleOptionsFunctions } from "./hooks/useCobbleOptionsFunctions";
import { useTableData } from "./hooks/useTableData";

interface Props {
	rows: Cobble[];
	isLoading: boolean;
	onRowClicked: (row: Cobble | null) => void;
	onGridReady: (params: GridApi<Cobble>) => void;
}

export const CobblesManagerTable = ({
	rows,
	isLoading,
	onRowClicked,
	onGridReady,
}: Props) => {
	const [showModal, setShowModal] = useState<"" | "Delete" | "Undelete">("");

	// --- Comment popover state ---
	const [commentPopover, setCommentPopover] = useState<{
		open: boolean;
		position: { top: number; left: number } | null;
		cobble: Cobble | null;
	}>({ open: false, position: null, cobble: null });

	const handleShowComment = (
		show: boolean,
		cobble?: Cobble,
		position?: { top: number; left: number },
	) => {
		if (show && cobble && position) {
			setCommentPopover({ open: true, position, cobble });
		} else {
			setCommentPopover({ open: false, position: null, cobble: null });
		}
	};

	const { getMenuOptions } = useCobbleOptionsFunctions({ setShowModal });
	const { showContextMenu, registerConfig } = useContextMenuMESF();

	const { columnDefs } = useTableData({
		setShowModal,
		onShowComment: handleShowComment,
	});
	const { defaultColDef } = useGridDefinitions({});

	// Keep getMenuOptions ref up-to-date without re-registering
	const getMenuOptionsRef = useRef(getMenuOptions);
	getMenuOptionsRef.current = getMenuOptions;

	// Register context menu once on mount
	useEffect(() => {
		registerConfig({
			id: "cobblesContext",
			getOptions: (data: unknown) => {
				if (!data || typeof data !== "object" || !("BilletId" in data)) {
					return [];
				}
				return getMenuOptionsRef.current(data as Cobble);
			},
		});
	}, []);

	const handleRowClicked = useCallback(
		(event: RowClickedEvent) => {
			onRowClicked(event.data as Cobble);
		},
		[onRowClicked],
	);

	const handleRowDoubleClicked = useCallback((event: RowDoubleClickedEvent) => {
		const cobble = event.data as Cobble;
		setShowModal(cobble.IsCobble ? "Delete" : "Undelete");
	}, []);

	const getContextMenuItems = useCallback(
		(
			params: GetContextMenuItemsParams<Cobble>,
		): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;

			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
				onRowClicked(data);
			}

			showContextMenu(
				params.event as unknown as React.MouseEvent,
				data,
				"cobblesContext",
			);

			return [];
		},
		[onRowClicked, showContextMenu],
	);

	const getRowStyle = (params: RowClassParams): RowStyle | undefined => {
		if (!(params.data as Cobble).IsCobble) {
			return { background: "#eaeaeaff" };
		}
		return undefined;
	};

	return (
		<>
			<AgGridReact<Cobble>
				loading={isLoading}
				rowData={rows}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				rowHeight={34}
				headerHeight={34}
				animateRows
				loadingOverlayComponent={CenteredLazyLoading}
				rowSelection="single"
				onGridReady={(params) => onGridReady(params.api)}
				onRowClicked={handleRowClicked}
				onRowDoubleClicked={handleRowDoubleClicked}
				getContextMenuItems={getContextMenuItems}
				getRowId={(params) => String(params.data.BilletId)}
				getRowStyle={getRowStyle}
				gridOptions={{
					rowSelection: {
						mode: "singleRow",
						checkboxes: false,
						enableClickSelection: true,
					},
					theme: customThemeWithHeaders,
				}}
			/>

			{/* Comment popover */}
			<Popover
				open={commentPopover.open}
				onClose={() => handleShowComment(false)}
				anchorReference="anchorPosition"
				anchorPosition={commentPopover.position ?? undefined}
				anchorOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				slotProps={{
					paper: {
						sx: {
							padding: 0,
							overflow: "hidden",
							maxWidth: 400,
							maxHeight: 300,
						},
					},
				}}
			>
				<Box
					sx={{
						width: 400,
						maxHeight: 300,
						overflowY: "auto",
						p: 1.5,
						backgroundColor: "grey.50",
					}}
				>
					{commentPopover.cobble ? (
						<Box>
							<Typography
								variant="caption"
								color="text.secondary"
								component="div"
							>
								{commentPopover.cobble.OperatorName || "Unknown"},{" "}
								{commentPopover.cobble.CorrectedAt
									? dxtToLocalServerTime(
											commentPopover.cobble.CorrectedAt,
											"MM/dd/yyyy HH:mm",
										)
									: "-"}
							</Typography>
							<Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
								{commentPopover.cobble.Comment}
							</Typography>
						</Box>
					) : (
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ fontStyle: "italic" }}
						>
							No comments
						</Typography>
					)}
				</Box>
			</Popover>

			<ConfirmActionModal
				action={showModal}
				onHide={() => {
					setShowModal("");
				}}
			/>
		</>
	);
};
